/**
 * useFetchUserFolders Hook - 使用 React Query 管理 fetchUserFolders 缓存
 *
 * 核心功能：
 * 1. 自动去重并发请求（相同 userId 的多个组件同时调用不会重复请求）
 * 2. 5分钟缓存控制（staleTime）
 * 3. 不改变现有数据流（依然更新 state.userFolders，组件通过 useSnapshot 获取）
 *
 * 使用方式：
 * ```tsx
 * // 组件中只需调用 Hook，不使用返回值
 * useFetchUserFolders();
 *
 * // 数据获取方式不变
 * const { userFolders } = useSnapshot(favoritesCenter.state);
 * ```
 */

import { useQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { QUERY_KEYS } from "@config/constant";
import { favoritesCenter } from "@store/favorite/FavoritesCenter";
import { rootStore } from "@store/rootStore";

export interface UseFetchUserFoldersOptions {
  /**
   * 是否启用查询
   * @default true（但需要用户已登录）
   */
  enabled?: boolean;

  /**
   * 是否强制刷新（忽略缓存）
   * @default false
   */
  forceRefresh?: boolean;

  /**
   * 缓存超时时间（毫秒）
   * @default 5 * 60 * 1000 (5分钟)
   */
  staleTime?: number;
}

/**
 * 使用 React Query 管理 fetchUserFolders 的缓存和并发控制
 *
 * @param options 配置选项
 * @returns React Query 的查询结果（通常不需要使用）
 *
 * @example
 * ```tsx
 * // 基础用法
 * useFetchUserFolders();
 *
 * // 强制刷新
 * useFetchUserFolders({ forceRefresh: true });
 *
 * // 条件启用
 * useFetchUserFolders({ enabled: someCondition });
 * ```
 */
export const useFetchUserFolders = (
  options: UseFetchUserFoldersOptions = {},
) => {
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const userId = ownerInfo?.id;

  const {
    enabled = true,
    forceRefresh = false,
    staleTime = 5 * 60 * 1000,
  } = options || {};

  return useQuery({
    queryKey: [QUERY_KEYS.USER_FOLDERS, userId, isLoggedIn],

    queryFn: async () => {
      // 直接调用 FavoritesCenter，它内部已有去重和缓存逻辑
      const result = await favoritesCenter.fetchUserFolders({ forceRefresh });

      if (result.success) {
        return result.data || [];
      }

      throw new Error(result.error || "Failed to fetch user folders");
    },

    staleTime,
    gcTime: 10 * 60 * 1000,
    enabled: enabled && isLoggedIn && !!userId,
    retry: 1,
    retryDelay: 1000,
  });
};
